<script setup lang="ts">
import { computed, inject, onUnmounted, ref, watch } from "vue";
import VuePdfEmbed from "vue-pdf-embed";

import { renderDocxFile } from "~/common/attachments";
import AiFeedbackButtons from "~/components/dumb/AiFeedbackButtons.vue";
import AttachmentActions from "~/components/dumb/AttachmentActions.vue";
import FileUploadProgress from "~/components/dumb/FileUploadProgress.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { IS_STANDALONE_MEDIA } from "~/constants/injection";
import { PlayIcon } from "~/icons";
import { EditorMode, ViewKind } from "~/shared/enums";
import type { Attachment } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore } from "~/stores";
import { downloadFile } from "~/utils/api";
import { filePreviews } from "~/utils/fileUpload";
import { isAudioType, isDocxType, isImageType, isPdfType, isVideoType } from "~/utils/media";

const MIN_WIDTH_PX = 80;

const props = defineProps<{
  attachment: Attachment;
  editorMode: EditorMode;
  notEditable?: boolean;
  resizable: boolean;
  widthPx: number | null;
}>();

const emit = defineEmits<{
  remove: [value: string];
  collapse: [value: string];
  resize: [value: number | null];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const isStandaloneMedia = inject(IS_STANDALONE_MEDIA, false);

const docxContainer = ref<HTMLDivElement | null>(null);
const docxLoaded = ref(false);

const isDocMode = computed(() => props.editorMode === EditorMode.DOC);
const isDetailMode = computed(() => props.editorMode === EditorMode.DETAIL);
const isTrash = computed(() => appStore.currentPage?.kind === ViewKind.TRASH);

const attachmentLoadSuccess = ref(true);
const extension = computed(() => {
  const { name } = props.attachment;
  const index = name.lastIndexOf(".");
  if (index === -1) {
    return "";
  }
  return `.${name.substring(index + 1)}`.toUpperCase();
});

const remove = (event?: Event) => {
  event?.stopPropagation();
  emit("remove", props.attachment.duid);
};

const isImage = computed(() => isImageType(props.attachment.kind));
const isVideo = computed(() => isVideoType(props.attachment.kind));
const isAudio = computed(() => isAudioType(props.attachment.kind));
const isPdf = computed(() => isPdfType(props.attachment.kind));
const isDocx = computed(() => isDocxType(props.attachment.kind));
const isVideoPreview = computed(() => isVideo.value && !isDetailMode.value);
const isUploading = computed(() => filePreviews.has(props.attachment.duid));

const fullscreenOrDownloadFile = () => {
  if (!isVideo.value && !isImage.value && !isAudio.value && !isPdf.value && !isDocx.value) {
    downloadFile(props.attachment.fileUrl, props.attachment.name);
    return;
  }

  appStore.setFullscreenMediaModalOpen({
    attachmentFileUrl: props.attachment.fileUrl,
    attachmentName: props.attachment.name,
    kind: props.attachment.kind,
    entityName: isStandaloneMedia
      ? ""
      : (appStore.taskOpenInDetail?.title ?? appStore.docOpenInFullscreen?.title ?? ""),
  });
};

const removeFeedbackButton = () => {
  if (!props.attachment.recommendationDuid) {
    return;
  }
  dataStore.updateAttachment({ duid: props.attachment.duid, recommendationDuid: null });
};

const wrapperElement = ref<HTMLDivElement | null>(null);
const imageElement = ref<HTMLImageElement | null>(null);
const isResizing = ref(false);
const isBottomResizing = ref(false);
const startXPx = ref(0);
const startYPx = ref(0);
const startWidthPx = ref(0);
const startHeightPx = ref(0);
const showResizeIndicators = ref(false);

const imageStyle = computed(() => {
  const shouldApplyWidth = isImage.value && isDocMode.value && props.widthPx !== null;
  return {
    width: shouldApplyWidth ? `${props.widthPx}px` : undefined,
    maxWidth: "100%",
    height: shouldApplyWidth ? "auto" : undefined,
  };
});

const onMouseMove = (e: MouseEvent) => {
  if (!imageElement.value) {
    return;
  }

  if (isBottomResizing.value) {
    const deltaY = e.clientY - startYPx.value;
    const aspectRatio = startHeightPx.value > 0 ? startWidthPx.value / startHeightPx.value : 1;
    const newHeight = Math.max(MIN_WIDTH_PX / aspectRatio, startHeightPx.value + deltaY);
    const newWidth = Math.max(MIN_WIDTH_PX, newHeight * aspectRatio);
    imageElement.value.style.width = `${newWidth}px`;
  } else if (isResizing.value) {
    const delta = e.clientX - startXPx.value;
    const newWidth = Math.max(MIN_WIDTH_PX, Math.round(startWidthPx.value + delta));
    imageElement.value.style.width = `${newWidth}px`;
  }
};

const endResize = () => {
  isResizing.value = false;
  isBottomResizing.value = false;
  showResizeIndicators.value = false;
  document.body.style.cursor = "";
  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("mouseup", endResize);
  const finalWidth = imageElement.value
    ? Math.max(MIN_WIDTH_PX, Math.round(imageElement.value.getBoundingClientRect().width))
    : null;
  emit("resize", finalWidth);
};

const canResize = computed(() => props.resizable && isImage.value && isDocMode.value && imageElement.value);

const setupResize = (e: MouseEvent, isBottom: boolean) => {
  if (!canResize.value || !imageElement.value) {
    return;
  }
  e.preventDefault();
  if (isBottom) {
    isBottomResizing.value = true;
    const rect = imageElement.value.getBoundingClientRect();
    startYPx.value = e.clientY;
    startWidthPx.value = rect.width;
    startHeightPx.value = rect.height;
    document.body.style.cursor = "row-resize";
  } else {
    isResizing.value = true;
    startXPx.value = e.clientX;
    startWidthPx.value = imageElement.value.getBoundingClientRect().width;
    document.body.style.cursor = "col-resize";
  }
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseup", endResize);
};

const startResize = (e: MouseEvent) => setupResize(e, false);
const startBottomResize = (e: MouseEvent) => setupResize(e, true);

watch(
  [isDocx, docxContainer, docxLoaded, isUploading],
  async () => {
    const container = docxContainer.value;
    if (!isDocx.value || !container || docxLoaded.value || isUploading.value) {
      return;
    }

    try {
      await renderDocxFile(props.attachment.fileUrl, container, "docx-preview-thumbnail", false);
      docxLoaded.value = true;
    } catch {
      attachmentLoadSuccess.value = false;
    }
  },
  { immediate: true }
);
onUnmounted(() => {
  removeFeedbackButton();
});
</script>

<template>
  <Tooltip
    :text="attachment.name"
    class="group/attachment-image relative flex-col gap-0.5"
    :class="isDocMode ? 'items-start' : 'items-center'">
    <FileUploadProgress :duid="attachment.duid">
      <template #default="{ previewFileUrl, uploading }">
        <div
          class="relative select-none"
          :class="{
            'cursor-default': uploading || (resizable && isDocMode && !uploading),
            'cursor-zoom-in': !uploading && (isImage || (isDetailMode && isVideo)) && !(resizable && isDocMode),
            'cursor-pointer': !(uploading || isImage || (isDetailMode && isVideo)),
            'size-20': isImage && !isDocMode,
            'w-full': !isImage || isAudio,
          }">
          <!-- Image -->
          <div
            v-if="isImage && attachmentLoadSuccess"
            ref="wrapperElement"
            class="relative flex"
            @mouseenter="showResizeIndicators = resizable && isDocMode && !uploading"
            @mouseleave="showResizeIndicators = isResizing || isBottomResizing"
            @focusin="showResizeIndicators = resizable && isDocMode && !uploading"
            @focusout="showResizeIndicators = isResizing || isBottomResizing">
            <img
              ref="imageElement"
              :src="previewFileUrl ?? attachment.fileUrl"
              :alt="attachment.name"
              class="rounded-sm object-cover"
              :class="[
                !isDocMode && 'size-20 border border-md',
                (isResizing || isBottomResizing) && 'outline outline-2 outline-primary-base',
              ]"
              :style="imageStyle"
              @error="attachmentLoadSuccess = false" />
            <div
              v-if="resizable && isDocMode && !uploading"
              class="absolute top-12 -right-2 bottom-0 w-4 cursor-col-resize"
              @mousedown.stop.prevent="startResize" />
            <div
              v-if="resizable && isDocMode && !uploading"
              class="absolute right-0 -bottom-2 left-12 h-4 cursor-row-resize"
              @mousedown.stop.prevent="startBottomResize" />
            <div
              v-if="showResizeIndicators && resizable && isDocMode && !uploading"
              class="pointer-events-none absolute top-1/2 right-0 z-10 h-8 w-2 translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-app-light-text-md bg-hvy dark:border-app-dark-text-md" />
            <div
              v-if="showResizeIndicators && resizable && isDocMode && !uploading"
              class="pointer-events-none absolute bottom-0 left-1/2 z-10 h-2 w-8 -translate-x-1/2 translate-y-1/2 rounded-full border-2 border-app-light-text-md bg-hvy dark:border-app-dark-text-md" />
          </div>
          <!-- Video -->
          <template v-else-if="isVideo && attachmentLoadSuccess">
            <video
              class="rounded-sm object-cover"
              :class="!isDocMode && 'size-20 border border-md'"
              :src="previewFileUrl ?? attachment.fileUrl"
              :controls="!isDetailMode"
              :playsinline="isDetailMode"
              :muted="isDetailMode"
              :preload="isDetailMode ? 'metadata' : 'auto'"
              :disablePictureInPicture="isDetailMode"
              :title="attachment.name"
              @error="attachmentLoadSuccess = false">
              <track kind="captions" />
            </video>
            <PlayIcon
              v-if="!uploading && isDetailMode"
              class="absolute inset-0 m-auto icon-xl text-hvy opacity-30 transition-opacity group-hover/attachment-image:opacity-70" />
          </template>
          <!-- Audio -->
          <audio
            v-else-if="isAudio && !isDetailMode && attachmentLoadSuccess"
            :src="previewFileUrl ?? attachment.fileUrl"
            :title="attachment.name"
            controls
            preload="auto"
            class="w-full"
            @error="attachmentLoadSuccess = false">
            <track kind="captions" />
          </audio>
          <!-- PDF -->
          <VuePdfEmbed
            v-else-if="isPdf && attachmentLoadSuccess"
            :source="previewFileUrl ?? attachment.fileUrl"
            :width="80"
            :scale="3"
            class="size-20 overflow-hidden rounded-sm border border-md"
            @loading-failed="attachmentLoadSuccess = false" />
          <!-- DOCX -->
          <div
            v-else-if="isDocx && attachmentLoadSuccess"
            ref="docxContainer"
            class="size-20 overflow-hidden rounded-sm border border-md bg-white" />
          <!-- Other -->
          <div
            v-else
            class="flex size-20 items-center justify-center rounded-sm border border-md"
            :style="{
              backgroundColor: props.attachment.colorHex,
            }">
            <div class="flex h-16 w-12 flex-col items-center justify-evenly rounded-sm bg-preview-paper">
              <div />
              <div
                class="min-h-5 w-full truncate bg-preview-accent px-1 py-0.5 text-center text-xs text-preview-accent">
                {{ extension }}
              </div>
            </div>
          </div>
          <div v-if="uploading" class="absolute inset-0 rounded-sm bg-gray-400/80 dark:bg-md/80" />
        </div>
      </template>

      <template #actions="{ uploading }">
        <div
          class="absolute inset-0 flex cursor-pointer items-center justify-center rounded-sm bg-md/30 opacity-0 group-hover/attachment-image:flex group-hover/attachment-image:opacity-100"
          :class="
            (uploading || isVideoPreview || (isAudio && !isDetailMode) || (resizable && isDocMode)) &&
            'pointer-events-none'
          "
          @click="() => (uploading || isVideoPreview || (isAudio && !isDetailMode) ? null : fullscreenOrDownloadFile())"
          @keydown.enter="
            () => (uploading || isVideoPreview || (isAudio && !isDetailMode) ? null : fullscreenOrDownloadFile())
          ">
          <AttachmentActions
            v-if="!uploading || (!notEditable && !pageStore.isPublicView && !isTrash)"
            :class="[isAudio && !isDetailMode ? '-top-7 right-1' : 'top-1 right-1']"
            :show-fullscreen="
              !uploading && !pageStore.isPublicForm && (isVideo || isImage || isAudio || isPdf || isDocx)
            "
            :show-download="!uploading"
            :show-collapse-expand="!uploading && !notEditable && isDocMode"
            :show-delete="!notEditable && !pageStore.isPublicView && !isTrash"
            collapse-expand-text="Collapse"
            @fullscreen="fullscreenOrDownloadFile"
            @download="downloadFile(attachment.fileUrl, attachment.name)"
            @collapse-expand="emit('collapse', attachment.duid)"
            @delete="remove" />
        </div>
      </template>
    </FileUploadProgress>
    <!-- TODO make these more like a tooltip/popup and don't show the tooltip with the attachment name on them -->
    <AiFeedbackButtons
      v-if="attachment.recommendationDuid"
      :recommendation-duids="[attachment.recommendationDuid]"
      @remove="removeFeedbackButton" />
  </Tooltip>
</template>

<style>
@reference "#frontend.css";
.docx-preview-thumbnail {
  @apply origin-top-left scale-[0.1];
}
</style>
